import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  FiSearch,
  FiPrinter,
  FiCreditCard,
  FiEye,
  FiX,
  FiRefreshCw,
  FiUser,
} from "react-icons/fi";

import {
  fetchRepresentatives,
  fetchRepresentativeAccounts,
  fetchRepresentativeAccountDetails,
  addRepresentativePayment,
} from "./settlementApi";
import { money, formatDate } from "./settlementFormatters";

const EMPTY_FILTERS = {
  RepresentativeId: "",
  FromDate: "",
  ToDate: "",
  Status: "",
  HasRemainingOnly: false,
  RepresentativeName: "",
};

const inputClass =
  "h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const statusClass = (status) => {
  if (status === "تم التقفيل") return "bg-green-100 text-green-700";
  if (status === "تقفيل جزئي") return "bg-yellow-100 text-yellow-700";
  return "bg-slate-100 text-slate-700";
};

const printRepresentative = (representative) => {
  if (!representative) return;

  const rows = (representative.settlements || [])
    .map(
      (item) => `
        <tr>
          <td>${item.id ?? "-"}</td>
          <td>${formatDate(item.settlementDate)}</td>
          <td>${item.totalHandedInvoicesCount ?? 0}</td>
          <td>${money(item.totalHandedAmount)} ج.م</td>
          <td>${money(item.shippingAmount)} ج.م</td>
          <td>${money(item.netRequiredAmount)} ج.م</td>
          <td>${money(item.paidAmount)} ج.م</td>
          <td>${money(item.remainingAmount)} ج.م</td>
          <td>${item.status || "-"}</td>
        </tr>
      `
    )
    .join("");

  const popup = window.open("", "_blank", "width=1200,height=800");
  if (!popup) return false;

  popup.document.write(`
    <!doctype html>
    <html lang="ar" dir="rtl">
      <head>
        <meta charset="UTF-8" />
        <title>حساب المندوب - ${representative.representativeName || ""}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 28px; color: #0f172a; }
          h1 { margin: 0 0 8px; font-size: 24px; }
          .meta { color: #64748b; margin-bottom: 22px; }
          .cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
          .card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; }
          .label { color: #64748b; font-size: 12px; margin-bottom: 8px; }
          .value { font-size: 18px; font-weight: 800; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #e2e8f0; padding: 9px; text-align: center; }
          th { background: #f8fafc; }
          .footer { margin-top: 25px; color: #64748b; font-size: 11px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <h1>${representative.representativeName || "حساب المندوب"}</h1>
        <div class="meta">
          الهاتف: ${representative.phone || "-"} &nbsp; | &nbsp;
          المحافظة: ${representative.governorateName || "-"}
        </div>

        <div class="cards">
          <div class="card"><div class="label">إجمالي الفواتير</div><div class="value">${representative.totalInvoices ?? 0}</div></div>
          <div class="card"><div class="label">صافي المطلوب</div><div class="value">${money(representative.totalNetRequired)} ج.م</div></div>
          <div class="card"><div class="label">إجمالي المدفوع</div><div class="value">${money(representative.totalPaid)} ج.م</div></div>
          <div class="card"><div class="label">المتبقي</div><div class="value">${money(representative.totalRemaining)} ج.م</div></div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th><th>التاريخ</th><th>الفواتير</th><th>إجمالي المبلغ</th>
              <th>الشحن</th><th>صافي المطلوب</th><th>المدفوع</th><th>المتبقي</th><th>الحالة</th>
            </tr>
          </thead>
          <tbody>${rows || '<tr><td colspan="9">لا توجد تسويات</td></tr>'}</tbody>
        </table>
        <div class="footer">تم إنشاء التقرير بتاريخ ${new Date().toLocaleString("ar-EG")}</div>
      </body>
    </html>
  `);

  popup.document.close();
  popup.focus();
  setTimeout(() => popup.print(), 250);
  return true;
};

const Representatives = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Representative picker: user searches by name only.
  // The selected representative ID is kept internally and sent to the API.
  const [representativesList, setRepresentativesList] = useState([]);
  const [representativeSearch, setRepresentativeSearch] = useState("");
  const [showRepresentativeDropdown, setShowRepresentativeDropdown] = useState(false);

  const [details, setDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [paymentSettlement, setPaymentSettlement] = useState(null);
  const [paymentRepresentative, setPaymentRepresentative] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    cashAmount: "",
    instaPayAmount: "",
    vodafoneCashAmount: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  // Load all representatives for the searchable picker.
  useEffect(() => {
    let mounted = true;

    const loadRepresentatives = async () => {
      try {
        const result = await fetchRepresentatives();
        if (!mounted) return;

        const list = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : [];

        setRepresentativesList(list);
      } catch (error) {
        console.error("GET REPRESENTATIVES ERROR:", error);
        if (mounted) setRepresentativesList([]);
      }
    };

    loadRepresentatives();

    return () => {
      mounted = false;
    };
  }, []);

  const representativeOptions = useMemo(() => {
    const search = representativeSearch.trim().toLowerCase();

    if (!search) return representativesList;

    return representativesList.filter((rep) => {
      const name = String(
        rep?.fullName ||
        rep?.representativeName ||
        rep?.name ||
        ""
      ).toLowerCase();

      return name.includes(search);
    });
  }, [representativesList, representativeSearch]);

  const selectedRepresentative = useMemo(() => {
    if (!filters.RepresentativeId) return null;

    return representativesList.find(
      (rep) => String(rep?.id ?? rep?.representativeId) === String(filters.RepresentativeId)
    ) || null;
  }, [representativesList, filters.RepresentativeId]);

  const handleSelectRepresentative = (rep) => {
    const id = rep?.id ?? rep?.representativeId;
    const name =
      rep?.fullName ||
      rep?.representativeName ||
      rep?.name ||
      "";

    setFilters((prev) => ({
      ...prev,
      RepresentativeId: id != null ? String(id) : "",
      RepresentativeName: "",
    }));

    setRepresentativeSearch(name);
    setShowRepresentativeDropdown(false);
  };

  const clearRepresentative = () => {
    setFilters((prev) => ({
      ...prev,
      RepresentativeId: "",
      RepresentativeName: "",
    }));
    setRepresentativeSearch("");
    setShowRepresentativeDropdown(false);
  };

  const representatives = Array.isArray(data?.representatives)
    ? data.representatives
    : [];

  const filteredRepresentatives = useMemo(() => {
    const name = filters.RepresentativeName.trim().toLowerCase();
    if (!name) return representatives;
    return representatives.filter((item) =>
      String(item?.representativeName || "").toLowerCase().includes(name)
    );
  }, [representatives, filters.RepresentativeName]);

  const search = async () => {
    try {
      setLoading(true);
      const result = await fetchRepresentativeAccounts(filters);
      setData(result);
      setSearched(true);
    } catch (error) {
      console.error("GET REPRESENTATIVE ACCOUNTS ERROR:", error);
      setData(null);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب حسابات المناديب",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setRepresentativeSearch("");
    setShowRepresentativeDropdown(false);
    setData(null);
    setSearched(false);
  };

  const openDetails = async (representative) => {
    try {
      setLoadingDetails(true);
      const result = await fetchRepresentativeAccountDetails(
        representative?.settlements?.[0]?.id
      );
      setDetails({ representative, latestSettlement: result });
      setShowDetails(true);
    } catch (error) {
      console.error("GET REPRESENTATIVE DETAIL ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب تفاصيل الحساب",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const openPayment = async (representative, preferredSettlement = null) => {
    const settlement = preferredSettlement || representative?.settlements?.find(
      (item) => Number(item?.remainingAmount || 0) > 0
    ) || representative?.settlements?.[0];

    if (!settlement?.id) {
      Swal.fire({
        icon: "warning",
        title: "لا توجد تسوية",
        text: "لا توجد تسوية مرتبطة بهذا المندوب لإضافة دفعة عليها",
        confirmButtonText: "حسناً",
      });
      return;
    }

    try {
      setLoadingDetails(true);
      const detail = await fetchRepresentativeAccountDetails(settlement.id);
      setPaymentRepresentative(representative);
      setPaymentSettlement(detail);
      setPaymentForm({
        cashAmount: "",
        instaPayAmount: "",
        vodafoneCashAmount: "",
        paymentDate: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      setShowPayment(true);
    } catch (error) {
      console.error("GET PAYMENT SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب بيانات التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const savePayment = async (e) => {
    e.preventDefault();

    const total =
      Number(paymentForm.cashAmount || 0) +
      Number(paymentForm.instaPayAmount || 0) +
      Number(paymentForm.vodafoneCashAmount || 0);

    if (total <= 0) {
      Swal.fire({
        icon: "warning",
        title: "بيانات ناقصة",
        text: "أدخل مبلغ الدفع في وسيلة دفع واحدة على الأقل",
        confirmButtonText: "حسناً",
      });
      return;
    }

    if (!paymentSettlement?.id) return;

    try {
      setSavingPayment(true);

      await addRepresentativePayment(paymentSettlement.id, {
        settlementId: Number(paymentSettlement.id),
        cashAmount: Number(paymentForm.cashAmount || 0),
        instaPayAmount: Number(paymentForm.instaPayAmount || 0),
        vodafoneCashAmount: Number(paymentForm.vodafoneCashAmount || 0),
        paymentDate: `${paymentForm.paymentDate}T00:00:00`,
        notes: paymentForm.notes.trim(),
      });

      Swal.fire({
        icon: "success",
        title: "تم تسجيل الدفعة",
        text: "تمت إضافة الدفعة بنجاح وتحديث حساب التسوية",
        confirmButtonText: "حسناً",
      });

      setShowPayment(false);
      await search();
    } catch (error) {
      console.error("ADD REPRESENTATIVE PAYMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء تسجيل الدفعة",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const totalRemaining = Number(data?.grandTotalRemaining || 0);

  return (
    <div>
      {/* FILTERS */}
      <section className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-bold text-slate-800">فلاتر المناديب</h2>
          <p className="mt-1 text-xs text-slate-500">
            استخدم الفلاتر للوصول إلى حسابات المندوبين والتسويات والمدفوعات
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <div className="relative">
            <label className="mb-1.5 block text-xs font-bold text-slate-700">
              المندوب
            </label>

            <div className="relative">
              <input
                type="text"
                value={representativeSearch}
                onChange={(e) => {
                  const value = e.target.value;

                  setRepresentativeSearch(value);
                  setShowRepresentativeDropdown(true);

                  // If the user changes the selected name, don't keep
                  // sending the previous representative ID.
                  if (
                    selectedRepresentative &&
                    value !==
                      (selectedRepresentative?.fullName ||
                        selectedRepresentative?.representativeName ||
                        selectedRepresentative?.name ||
                        "")
                  ) {
                    setFilters((prev) => ({
                      ...prev,
                      RepresentativeId: "",
                      RepresentativeName: "",
                    }));
                  }
                }}
                onFocus={() => setShowRepresentativeDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setShowRepresentativeDropdown(false);
                  }
                }}
                placeholder="ابحث باسم المندوب"
                autoComplete="off"
                className={`${inputClass} pr-8`}
              />

              {representativeSearch ? (
                <button
                  type="button"
                  onClick={clearRepresentative}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  title="مسح المندوب"
                >
                  <FiX size={14} />
                </button>
              ) : (
                <FiSearch
                  size={14}
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                />
              )}
            </div>

            {showRepresentativeDropdown && (
              <div className="absolute right-0 top-full z-[60] mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                {representativeOptions.length === 0 ? (
                  <div className="px-3 py-3 text-center text-xs text-slate-500">
                    لا يوجد مندوب بهذا الاسم
                  </div>
                ) : (
                  representativeOptions.map((rep) => {
                    const repName =
                      rep?.fullName ||
                      rep?.representativeName ||
                      rep?.name ||
                      "مندوب";

                    const repPhone =
                      rep?.phoneNumber ||
                      rep?.phone ||
                      "";

                    const repId = rep?.id ?? rep?.representativeId;

                    const isSelected =
                      String(repId) === String(filters.RepresentativeId);

                    return (
                      <button
                        key={String(repId ?? repName)}
                        type="button"
                        onClick={() => handleSelectRepresentative(rep)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-right text-xs transition ${
                          isSelected
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span className="min-w-0 truncate font-bold">
                          {repName}
                        </span>

                        {repPhone && (
                          <span className="shrink-0 text-[10px] text-slate-400">
                            {repPhone}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-700">من التاريخ</label>
            <input
              type="date"
              value={filters.FromDate}
              onChange={(e) => setFilters((p) => ({ ...p, FromDate: e.target.value }))}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-700">إلى التاريخ</label>
            <input
              type="date"
              value={filters.ToDate}
              onChange={(e) => setFilters((p) => ({ ...p, ToDate: e.target.value }))}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold text-slate-700">الحالة</label>
            <select
              value={filters.Status}
              onChange={(e) => setFilters((p) => ({ ...p, Status: e.target.value }))}
              className={inputClass}
            >
              <option value="">كل الحالات</option>
              <option value="تم التقفيل">تم التقفيل</option>
              <option value="تقفيل جزئي">تقفيل جزئي</option>
              <option value="قيد التصفية">قيد التصفية</option>
            </select>
          </div>

          <label className="flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700">
            <input
              type="checkbox"
              checked={filters.HasRemainingOnly}
              onChange={(e) => setFilters((p) => ({ ...p, HasRemainingOnly: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            لديهم متبقي فقط
          </label>
        </div>

        <div className="flex flex-col gap-2 border-t border-slate-200 p-4 sm:flex-row">
          <button
            type="button"
            onClick={search}
            disabled={loading}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
          >
            <FiSearch />
            {loading ? "جاري البحث..." : "بحث"}
          </button>

          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
          >
            <FiRefreshCw />
            مسح الفلاتر
          </button>
        </div>
      </section>

      {/* SUMMARY */}
      {searched && data && (
        <section className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-2 divide-x divide-slate-200 rtl:divide-x-reverse sm:grid-cols-3 lg:grid-cols-5">
            <div className="p-3 text-center">
              <p className="text-[11px] font-medium text-gray-500">عدد المناديب</p>
              <p className="mt-1 text-base font-black text-slate-800">{data.totalRepresentatives ?? 0}</p>
            </div>
            <div className="p-3 text-center">
              <p className="text-[11px] font-medium text-gray-500">عليهم متبقي</p>
              <p className="mt-1 text-base font-black text-red-600">{data.totalRepresentativesWithRemaining ?? 0}</p>
            </div>
            <div className="p-3 text-center">
              <p className="text-[11px] font-medium text-gray-500">إجمالي المطلوب</p>
              <p className="mt-1 text-base font-black text-blue-700">{money(data.grandTotalNetRequired)} ج.م</p>
            </div>
            <div className="p-3 text-center">
              <p className="text-[11px] font-medium text-gray-500">إجمالي المدفوع</p>
              <p className="mt-1 text-base font-black text-purple-700">{money(data.grandTotalPaid)} ج.م</p>
            </div>
            <div className="p-3 text-center">
              <p className="text-[11px] font-medium text-gray-500">إجمالي المتبقي</p>
              <p className={`mt-1 text-base font-black ${totalRemaining > 0 ? "text-red-600" : "text-green-700"}`}>
                {money(totalRemaining)} ج.م
              </p>
            </div>
          </div>
        </section>
      )}

      {/* TABLE */}
      {searched && (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-2 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-800">حسابات المناديب</h2>
              <p className="mt-1 text-xs text-slate-500">كل مندوب مع إجمالي التسويات والمدفوعات والمتبقي</p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              {filteredRepresentatives.length} مندوب
            </span>
          </div>

          {loading ? (
            <div className="py-16 text-center">
              <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
              <p className="mt-3 text-sm text-slate-500">جاري تحميل الحسابات...</p>
            </div>
          ) : filteredRepresentatives.length === 0 ? (
            <div className="py-14 text-center text-sm text-slate-500">لا توجد حسابات مطابقة للفلاتر</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1250px] text-right text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-bold text-slate-600">
                    <th className="px-3 py-3">#</th>
                    <th className="px-3 py-3">المندوب</th>
                    <th className="px-3 py-3">الهاتف</th>
                    <th className="px-3 py-3">المحافظة</th>
                    <th className="px-3 py-3">التسويات</th>
                    <th className="px-3 py-3">الفواتير</th>
                    <th className="px-3 py-3">صافي المطلوب</th>
                    <th className="px-3 py-3">المدفوع</th>
                    <th className="px-3 py-3">المتبقي</th>
                    <th className="px-3 py-3">آخر تسوية</th>
                    <th className="px-3 py-3">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRepresentatives.map((item, index) => (
                    <tr key={item.representativeId} className="border-b border-slate-100 transition hover:bg-slate-50">
                      <td className="px-3 py-3 font-bold text-slate-500">{index + 1}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                            {item.representativeName?.charAt(0) || "م"}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{item.representativeName || "-"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{item.phone || "-"}</td>
                      <td className="px-3 py-3 text-slate-600">{item.governorateName || "-"}</td>
                      <td className="px-3 py-3 font-bold">{item.totalSettlements ?? 0}</td>
                      <td className="px-3 py-3 font-bold">{item.totalInvoices ?? 0}</td>
                      <td className="px-3 py-3 font-black text-blue-700">{money(item.totalNetRequired)} ج.م</td>
                      <td className="px-3 py-3 font-black text-purple-700">{money(item.totalPaid)} ج.م</td>
                      <td className="px-3 py-3">
                        <span className={`font-black ${Number(item.totalRemaining || 0) > 0 ? "text-red-600" : "text-green-700"}`}>
                          {money(item.totalRemaining)} ج.م
                        </span>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{formatDate(item.lastSettlementDate)}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openDetails(item)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                            title="عرض التفاصيل"
                          >
                            <FiEye size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openPayment(item)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-green-600 text-white transition hover:bg-green-700 disabled:opacity-50"
                            title="إضافة دفعة"
                            disabled={loadingDetails}
                          >
                            <FiCreditCard size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const opened = printRepresentative(item);
                              if (!opened) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "تعذر فتح نافذة الطباعة",
                                  text: "اسمح بالنوافذ المنبثقة ثم حاول مرة أخرى",
                                  confirmButtonText: "حسناً",
                                });
                              }
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                            title="طباعة"
                          >
                            <FiPrinter size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* DETAILS MODAL */}
      {showDetails && details && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">تفاصيل حساب المندوب</h2>
                <p className="mt-1 text-xs text-slate-500">{details.representative?.representativeName}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <FiX />
              </button>
            </div>

            <div className="max-h-[75vh] overflow-y-auto p-5">
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-500">صافي المطلوب</p>
                  <p className="mt-1 text-lg font-black text-blue-700">{money(details.representative?.totalNetRequired)} ج.م</p>
                </div>
                <div className="rounded-xl bg-purple-50 p-4">
                  <p className="text-xs font-bold text-purple-600">المدفوع</p>
                  <p className="mt-1 text-lg font-black text-purple-700">{money(details.representative?.totalPaid)} ج.م</p>
                </div>
                <div className="rounded-xl bg-red-50 p-4">
                  <p className="text-xs font-bold text-red-600">المتبقي</p>
                  <p className="mt-1 text-lg font-black text-red-700">{money(details.representative?.totalRemaining)} ج.م</p>
                </div>
                <div className="rounded-xl bg-green-50 p-4">
                  <p className="text-xs font-bold text-green-600">التسويات</p>
                  <p className="mt-1 text-lg font-black text-green-700">{details.representative?.totalSettlements ?? 0}</p>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full min-w-[1000px] text-right text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-3">#</th>
                      <th className="px-3 py-3">التاريخ</th>
                      <th className="px-3 py-3">الفواتير</th>
                      <th className="px-3 py-3">المطلوب</th>
                      <th className="px-3 py-3">المدفوع</th>
                      <th className="px-3 py-3">المتبقي</th>
                      <th className="px-3 py-3">الحالة</th>
                      <th className="px-3 py-3">دفع</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(details.representative?.settlements || []).map((item) => (
                      <tr key={item.id} className="border-t border-slate-100">
                        <td className="px-3 py-3 font-bold">{item.id}</td>
                        <td className="px-3 py-3">{formatDate(item.settlementDate)}</td>
                        <td className="px-3 py-3">{item.totalHandedInvoicesCount ?? 0}</td>
                        <td className="px-3 py-3 font-bold text-blue-700">{money(item.netRequiredAmount)} ج.م</td>
                        <td className="px-3 py-3 font-bold text-purple-700">{money(item.paidAmount)} ج.م</td>
                        <td className="px-3 py-3 font-bold text-red-600">{money(item.remainingAmount)} ج.م</td>
                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusClass(item.status)}`}>
                            {item.status || "-"}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {Number(item.remainingAmount || 0) > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                setShowDetails(false);
                                openPayment(details.representative, item);
                              }}
                              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700"
                            >
                              دفع
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">لا يوجد</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT MODAL */}
      {showPayment && paymentSettlement && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">إضافة دفعة للمندوب</h2>
                <p className="mt-1 text-xs text-slate-500">
                  {paymentRepresentative?.representativeName} — تسوية #{paymentSettlement.id}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !savingPayment && setShowPayment(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={savePayment} className="p-5">
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-blue-50 p-4 text-center">
                  <p className="text-xs font-bold text-blue-600">صافي المطلوب</p>
                  <p className="mt-1 text-xl font-black text-blue-700">{money(paymentSettlement.netRequiredAmount)} ج.م</p>
                </div>
                <div className="rounded-xl bg-purple-50 p-4 text-center">
                  <p className="text-xs font-bold text-purple-600">المدفوع</p>
                  <p className="mt-1 text-xl font-black text-purple-700">{money(paymentSettlement.paidAmount)} ج.م</p>
                </div>
                <div className="rounded-xl bg-red-50 p-4 text-center">
                  <p className="text-xs font-bold text-red-600">المتبقي</p>
                  <p className="mt-1 text-xl font-black text-red-700">{money(paymentSettlement.remainingAmount)} ج.م</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">كاش</label>
                  <input name="cashAmount" type="number" min="0" step="0.01" value={paymentForm.cashAmount} onChange={handlePaymentChange} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">InstaPay</label>
                  <input name="instaPayAmount" type="number" min="0" step="0.01" value={paymentForm.instaPayAmount} onChange={handlePaymentChange} placeholder="0" className={inputClass} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">Vodafone Cash</label>
                  <input name="vodafoneCashAmount" type="number" min="0" step="0.01" value={paymentForm.vodafoneCashAmount} onChange={handlePaymentChange} placeholder="0" className={inputClass} />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">تاريخ الدفع</label>
                  <input name="paymentDate" type="date" value={paymentForm.paymentDate} onChange={handlePaymentChange} className={inputClass} />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700">ملاحظات</label>
                  <input name="notes" value={paymentForm.notes} onChange={handlePaymentChange} placeholder="ملاحظات الدفع" className={inputClass} />
                </div>
              </div>

              <div className="mt-5 rounded-xl bg-blue-50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-blue-700">إجمالي الدفعة</span>
                  <span className="text-xl font-black text-blue-700">
                    {money(
                      Number(paymentForm.cashAmount || 0) +
                        Number(paymentForm.instaPayAmount || 0) +
                        Number(paymentForm.vodafoneCashAmount || 0)
                    )} ج.م
                  </span>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={savingPayment}
                  className="flex-1 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {savingPayment ? "جاري الحفظ..." : "تسجيل الدفعة"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPayment(false)}
                  disabled={savingPayment}
                  className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Representatives;
